import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Grid, Card, CardContent,
  Divider, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Alert, Button, LinearProgress,
  Tooltip, Link, CircularProgress
} from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SecurityIcon from '@mui/icons-material/Security';
import GitHubIcon from '@mui/icons-material/GitHub';
import DescriptionIcon from '@mui/icons-material/Description';
import KeyIcon from '@mui/icons-material/Key';
import SendIcon from '@mui/icons-material/Send';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Link as RouterLink } from 'react-router-dom';
import { useNetwork } from '../context/NetworkContext';
import IntegrationGuides from '../components/IntegrationGuides';
import { openNetworkSocket } from '../utils/networkSocket';

// Empty initial state - no mock data
const EMPTY_ORACLE_PRICE = {
  price_micro_usd: 0,
  price_usd: 0,
  oracle_count: 0,
  status: 'unavailable',
  last_update_height: 0,
  is_stale: true,
  '24h_high': 0,
  '24h_low': 0,
  volatility: 0
};

// Default oracle configuration. Live RPC is the
// authority, with these constants only used before data arrives.
const ORACLE_TOTAL_SLOTS = 35;
const ACTIVE_ORACLE_COUNT = 35;
const MAX_ACTIVE_ORACLE_ID = 34; // fallback for older RPCs with no in_consensus flag
const ORACLE_THRESHOLD = 7;      // consensus requires 7 signatures
const EXPECTED_MUSIG2_CONTEXT_VERSION = 2;
const ORACLE_EPOCH_BLOCKS = 40;
const TARGET_BLOCK_SECONDS = 15;

// Oracle name mapping for cases where daemon returns "Unknown".
// The node's vOracleNodes list uses placeholder keys for IDs 9 and 10;
// their real-world operator names must be supplied here.
const ORACLE_NAMES = {
  7: 'LookInto',        // Oracle 7
  9: 'Ogilvie',         // Oracle 9
  10: 'ChopperBrian',   // Oracle 10
  11: 'hallvardo',      // Oracle 11
  12: 'DaPunzy',        // Oracle 12
  13: 'DigiByteForce',  // Oracle 13
  14: 'Neel',           // Oracle 14
  15: 'DigiSwarm',      // Oracle 15
  16: 'GTO90',          // Oracle 16
  17: 'digibyte-maxi',  // Oracle 17
  18: 'Anthony',        // Oracle 18
  19: 'mbah_jambon',    // Oracle 19
  20: 'Camden',         // Oracle 20
  21: 'Twoface123',     // Oracle 21
  22: 'LivingTheLife',  // Oracle 22
  23: 'ChozenOne43',    // Oracle 23
  24: 'ckunchained',    // Oracle 24
  25: 'JMag',           // Oracle 25
  26: 'HashedMax',      // Oracle 26
  27: 'DennisPitallano',// Oracle 27
  28: 'DigiHash',       // Oracle 28
  29: 'Michael E',      // Oracle 29
  30: 'DigibyteDaily',  // Oracle 30
  31: 'Peer2Peer / DigiRoos', // Oracle 31
  32: '3DogsKanab',     // Oracle 32
  33: 'LiberatedLark',  // Oracle 33
  34: 'Manu_DGB_oracle' // Oracle 34
};

const formatAgeSeconds = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value < 0) return 'unknown';

  const totalSeconds = Math.floor(value);
  if (totalSeconds < 60) return `${totalSeconds}s ago`;

  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s ago`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) return `${hours}h ${remainingMinutes}m ago`;

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days}d ${remainingHours}h ago`;
};

const formatTimestampAge = (timestamp) => {
  const value = Number(timestamp);
  if (!Number.isFinite(value) || value <= 0) return 'unknown';
  const nowSeconds = Math.floor(Date.now() / 1000);
  return formatAgeSeconds(Math.max(0, nowSeconds - value));
};

const formatDurationSeconds = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return '~0s';

  const totalSeconds = Math.round(value);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) return `~${hours}h ${minutes}m`;
  if (minutes > 0) return `~${minutes}m ${remainingSeconds}s`;
  return `~${remainingSeconds}s`;
};

const getHeartbeatChipColor = (status, signatureValid) => {
  if (status === 'fresh' && signatureValid) return 'success';
  if (status === 'stale') return 'warning';
  if (status === 'invalid_signature') return 'error';
  return 'default';
};

/**
 * OraclesPage Component - DigiDollar Oracle Network Status
 *
 * Displays real-time information about the decentralized oracle network
 * that provides DGB/USD price feeds for the DigiDollar system.
 * Receives data via WebSocket push from the backend.
 */
const OraclesPage = () => {
  const network = useNetwork();
  const { wsBaseUrl, theme: networkTheme, digiDollarLabel, displayName } = network;
  const oracleConfig = network.oracle || {};
  const primaryColor = networkTheme.primary;
  const secondaryColor = networkTheme.secondary;
  const oracleTotalSlots = oracleConfig.totalSlots || ORACLE_TOTAL_SLOTS;
  const activeOracleSlots = oracleConfig.activeSlots || ACTIVE_ORACLE_COUNT;
  const oracleThreshold = oracleConfig.threshold || ORACLE_THRESHOLD;
  const oracleConsensusLabel = `${oracleThreshold} of ${oracleTotalSlots}`;

  // State for oracle data - start empty, no mock data
  const [oraclePrice, setOraclePrice] = useState(EMPTY_ORACLE_PRICE);
  const [oracles, setOracles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [ddDeploymentStatus, setDdDeploymentStatus] = useState(null);
  const [ddDeploymentInfo, setDdDeploymentInfo] = useState(null);
  const [oracleBlockHeight, setOracleBlockHeight] = useState(0);
  const [oracleSigners, setOracleSigners] = useState(null);

  // WebSocket connection for real-time oracle data
  useEffect(() => {
    const socket = openNetworkSocket(wsBaseUrl);

    socket.onopen = () => {
      console.log('WebSocket connection established for oracles page');
    };

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'ddDeploymentData') {
          setDdDeploymentStatus(message.data.status);
          setDdDeploymentInfo(message.data);
        }
        if (message.type === 'oracleData') {
          const { price: priceData, allPrices: allOraclePricesData, oracles: oraclesConfigData, oracleSigners: oracleSignersData } = message.data;
          const allOraclePrices = (allOraclePricesData && allOraclePricesData.oracles) || [];
          const currentBlockHeight = allOraclePricesData?.block_height || priceData?.block_height || priceData?.last_update_height || 0;
          const latestSignerBundle = oracleSignersData?.bundles?.[0] || null;
          const latestBundleSignerIds = new Set((latestSignerBundle?.signer_ids || []).map(Number));

          // Update state with real price data
          setOraclePrice({
            price_micro_usd: priceData.price_micro_usd || 0,
            price_usd: priceData.price_usd || 0,
            oracle_count: priceData.oracle_count || 0,
            status: priceData.status || 'unknown',
            last_update_height: priceData.last_update_height || 0,
            is_stale: priceData.is_stale || false,
            '24h_high': priceData['24h_high'] || 0,
            '24h_low': priceData['24h_low'] || 0,
            volatility: priceData.volatility || 0
          });
          setOracleBlockHeight(currentBlockHeight);
          setOracleSigners(oracleSignersData || null);

          // Use getoracles (config with all oracles) as base, merge price data from getalloracleprices
          const mappedOracles = (oraclesConfigData || []).map(configOracle => {
            const priceOracle = allOraclePrices.find(o => o.oracle_id === configOracle.oracle_id) || {};
            const priceMicroUsd = priceOracle.price_micro_usd ?? configOracle.last_price_micro_usd ?? 0;
            const priceUsd = priceOracle.price_usd ?? configOracle.last_price_usd ?? 0;
            const lastPriceTimestamp = priceOracle.timestamp ?? configOracle.last_update ?? 0;
            const priceStatus = priceOracle.status || configOracle.status || 'no_data';

            return {
              oracle_id: configOracle.oracle_id,
              name: configOracle.name !== 'Unknown' ? configOracle.name : (ORACLE_NAMES[configOracle.oracle_id] || `Oracle ${configOracle.oracle_id}`),
              pubkey: configOracle.pubkey || '',
              endpoint: configOracle.endpoint,
              is_active: configOracle.is_active !== false,
              in_consensus: configOracle.in_consensus ?? (configOracle.is_active !== false && configOracle.oracle_id <= MAX_ACTIVE_ORACLE_ID),
              epoch_eligible: Boolean(configOracle.selected_for_epoch),
              selected_for_epoch: Boolean(configOracle.selected_for_epoch),
              signed_latest_bundle: latestBundleSignerIds.has(Number(configOracle.oracle_id)),
              is_running_locally: Boolean(configOracle.is_running_locally),
              price_micro_usd: priceMicroUsd,
              price_usd: priceUsd,
              timestamp: lastPriceTimestamp,
              deviation_pct: priceOracle.deviation_pct || 0,
              signature_valid: priceOracle.signature_valid || false,
              price_source: configOracle.price_source || priceOracle.price_source || 'none',
              status: priceStatus,
              is_running: priceStatus === 'reporting',
              heartbeat_status: configOracle.heartbeat_status || 'unknown',
              software_version: configOracle.software_version || '',
              client_version: configOracle.client_version || 0,
              p2p_protocol_version: configOracle.p2p_protocol_version || 0,
              oracle_protocol_version: configOracle.oracle_protocol_version || 0,
              musig2_context_version: configOracle.musig2_context_version || 0,
              heartbeat_timestamp: configOracle.heartbeat_timestamp || 0,
              heartbeat_age_seconds: configOracle.heartbeat_age_seconds ?? -1,
              heartbeat_signature_valid: Boolean(configOracle.heartbeat_signature_valid)
            };
          });

          // Only show consensus roster oracles. Older RPCs did not expose
          // in_consensus, so mapping above falls back to IDs 0-34.
          const activeOracles = mappedOracles.filter(o => o.in_consensus);
          setOracles(activeOracles);
          setLastUpdated(new Date());
          setLoading(false);
          setError(null);
        }
      } catch (err) {
        console.error('Error processing oracle WebSocket message:', err);
      }
    };

    socket.onerror = () => {
      setError('Unable to connect to oracle data feed. Network may be unavailable.');
      setLoading(false);
    };

    socket.onclose = (event) => {
      if (event.code !== 1000) {
        console.log('Oracle WebSocket connection closed, code:', event.code);
      }
    };

    return () => socket.close();
  }, [wsBaseUrl]);

  // Authoritative BIP9 deployment status from the node's getdeploymentinfo RPC.
  // The WebSocket ddDeploymentData message above is a live source, but it can
  // silently never arrive — polling REST here keeps the "DigiDollar is not
  // active yet" banner reliable regardless. Mirrors DDActivationPage.
  const fetchDeploymentInfo = useCallback(async () => {
    try {
      const res = await fetch(network.getApiUrl('/getdeploymentinfo'));
      if (res.ok) {
        const dep = await res.json();
        const dd = dep?.deployments?.digidollar;
        const status = dd ? (dd.active ? 'active' : dd.bip9?.status) : null;
        if (status) setDdDeploymentStatus(status);
      }
    } catch (err) {
      console.error('OraclesPage getdeploymentinfo error:', err);
    }
  }, [network]);

  useEffect(() => {
    fetchDeploymentInfo();
    const id = setInterval(fetchDeploymentInfo, 30000);
    return () => clearInterval(id);
  }, [fetchDeploymentInfo]);

  // Format price from micro-USD to display format
  const formatPrice = (microUsd) => {
    if (!microUsd || microUsd === 0) return 'Not Reporting';
    return '$' + (microUsd / 1000000).toFixed(6);
  };

  // Check if we have valid data
  const hasData = oraclePrice.oracle_count > 0 || oracles.length > 0;

  // Count oracles that are actively signing prices and publishing heartbeats.
  const activeOracleCount = oracles.length || activeOracleSlots;
  const isOracleSigningPrice = (oracle) => oracle.status === 'reporting' && oracle.signature_valid;
  const priceSigningCount = oracles.filter(isOracleSigningPrice).length;
  const latestBundle = oracleSigners?.bundles?.[0] || null;
  const hasLatestBundleSignerData = Boolean(latestBundle && Array.isArray(latestBundle.signer_ids));
  const latestBundleRequired = Number(oracleSigners?.required_signers) || oracleThreshold;
  const latestBundleSignerCount = oracles.filter(o => o.signed_latest_bundle).length || Number(latestBundle?.signer_count) || 0;
  const freshHeartbeatCount = oracles.filter(o => o.heartbeat_status === 'fresh' && o.heartbeat_signature_valid).length;
  const rc43ContextCount = oracles.filter(o =>
    o.heartbeat_status === 'fresh' &&
    o.heartbeat_signature_valid &&
    o.musig2_context_version >= EXPECTED_MUSIG2_CONTEXT_VERSION
  ).length;
  const locallyRunningCount = oracles.filter(o => o.is_running_locally).length;
  const consensusReady = (!hasLatestBundleSignerData || latestBundleSignerCount >= latestBundleRequired) &&
    priceSigningCount >= oracleThreshold &&
    freshHeartbeatCount >= oracleThreshold &&
    rc43ContextCount >= oracleThreshold;
  const versionBuckets = Object.values(oracles.reduce((buckets, oracle) => {
    const version = oracle.software_version || 'No version reported';
    if (!buckets[version]) {
      buckets[version] = {
        version,
        count: 0,
        reporting: 0,
        fresh: 0
      };
    }
    buckets[version].count += 1;
    if (isOracleSigningPrice(oracle)) buckets[version].reporting += 1;
    if (oracle.heartbeat_status === 'fresh' && oracle.heartbeat_signature_valid) buckets[version].fresh += 1;
    return buckets;
  }, {})).sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.version.localeCompare(b.version);
  });
  const formatOperatorCount = (count) => `${count} operator${count === 1 ? '' : 's'}`;

  const getOracleEpochInfo = () => {
    const session = ddDeploymentInfo?.musig2_session || {};
    const currentHeight = Number(oracleBlockHeight || oraclePrice.last_update_height || session.creation_height || 0);
    const sessionEpoch = Number(session.epoch);
    const epoch = Number.isFinite(sessionEpoch) && sessionEpoch >= 0
      ? sessionEpoch
      : (currentHeight > 0 ? Math.floor(currentHeight / ORACLE_EPOCH_BLOCKS) : null);

    if (epoch === null) return null;

    const sessionStartHeight = Number(session.creation_height);
    const epochStartHeight = Number.isFinite(sessionStartHeight) && sessionStartHeight >= 0
      ? sessionStartHeight
      : epoch * ORACLE_EPOCH_BLOCKS;
    const epochEndHeight = epochStartHeight + ORACLE_EPOCH_BLOCKS - 1;
    const nextEpochHeight = epochEndHeight + 1;
    const minedBlocks = currentHeight > 0
      ? Math.max(0, Math.min(ORACLE_EPOCH_BLOCKS, currentHeight - epochStartHeight + 1))
      : 0;
    const blocksUntilNextEpoch = currentHeight > 0
      ? Math.max(0, nextEpochHeight - currentHeight)
      : ORACLE_EPOCH_BLOCKS;
    const requiredSigners = Number(ddDeploymentInfo?.oracle_consensus_required) || oracleThreshold;

    return {
      epoch,
      currentHeight,
      epochStartHeight,
      epochEndHeight,
      nextEpochHeight,
      minedBlocks,
      blocksUntilNextEpoch,
      estimatedSeconds: blocksUntilNextEpoch * TARGET_BLOCK_SECONDS,
      progress: Math.min(100, Math.max(0, (minedBlocks / ORACLE_EPOCH_BLOCKS) * 100)),
      sessionState: session.state || 'unknown',
      nonceCount: Number(session.nonce_count) || 0,
      partialSigCount: Number(session.partial_sig_count) || 0,
      requiredSigners
    };
  };

  const epochInfo = getOracleEpochInfo();

  const SitrepMetric = ({ label, value, detail, ok, progressValue, progressTotal = activeOracleCount }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: ok ? 'rgba(46, 125, 50, 0.06)' : '#fafafa'
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{ color: ok ? '#2e7d32' : '#ed6c02', lineHeight: 1.1 }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {detail}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={progressTotal ? Math.min(100, ((progressValue || 0) / progressTotal) * 100) : 0}
        color={ok ? 'success' : 'warning'}
        sx={{ mt: 1.5, height: 6, borderRadius: 3 }}
      />
    </Paper>
  );

  // Hero Section
  const HeroSection = () => (
    <Card
      elevation={2}
      sx={{
        backgroundColor: '#f2f4f8',
        borderRadius: '12px',
        mb: 4,
        overflow: 'hidden',
        backgroundImage: `linear-gradient(135deg, ${primaryColor}12 0%, ${secondaryColor}24 100%)`,
        border: `1px solid ${primaryColor}26`
      }}
    >
      <CardContent sx={{ py: 3, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <SensorsIcon sx={{ fontSize: '3rem', color: primaryColor, mr: 2 }} />
          <Typography
            variant="h2"
            component="h1"
            fontWeight="800"
            sx={{
              color: primaryColor,
              letterSpacing: '0.5px',
              fontSize: { xs: '1.8rem', sm: '2.3rem', md: '2.8rem' }
            }}
          >
            DigiDollar {digiDollarLabel} Oracles
          </Typography>
        </Box>

        <Typography
          variant="h5"
          sx={{
            color: secondaryColor,
            mb: 2,
            fontWeight: 600
          }}
        >
          Decentralized Price Feed Network
        </Typography>

        <Divider sx={{ maxWidth: '150px', mx: 'auto', mb: 2, borderColor: secondaryColor, borderWidth: 2 }} />

        <Typography
          variant="body1"
          sx={{
            maxWidth: '800px',
            mx: 'auto',
            color: '#555'
          }}
        >
          The oracle network provides real-time DGB/USD price feeds to the DigiDollar system using BIP-340 Schnorr signature consensus.
        </Typography>
      </CardContent>
    </Card>
  );

  // Current Price Card
  const CurrentPriceCard = () => (
    <Card elevation={3} sx={{ p: 3, mb: 4, borderRadius: '12px', borderTop: `4px solid ${secondaryColor}` }}>
      {/* Error Alert - only show when no data is available */}
      {error && !hasData && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AttachMoneyIcon sx={{ fontSize: '2rem', color: primaryColor, mr: 1 }} />
          <Typography variant="h5" fontWeight="bold" sx={{ color: primaryColor }}>
            {displayName} Oracle Price
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={40} sx={{ color: primaryColor }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading oracle data...</Typography>
        </Box>
      ) : !hasData && ddDeploymentStatus && ddDeploymentStatus !== 'active' ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <HourglassTopIcon sx={{ fontSize: '3rem', color: '#9e9e9e', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">Oracle Network Waiting for Activation</Typography>
          <Typography variant="body2" color="text.secondary">
            DigiDollar is currently in the {ddDeploymentStatus.toUpperCase().replace('_', ' ')} stage, so live oracle price reporting is not available yet.
          </Typography>
        </Box>
      ) : !hasData && error ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CloudOffIcon sx={{ fontSize: '3rem', color: '#9e9e9e', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">Oracle Network Unavailable</Typography>
          <Typography variant="body2" color="text.secondary">Unable to fetch oracle data from the network</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Tooltip title="Consensus price from oracle network - median of all active oracle price feeds with outlier filtering" arrow placement="top">
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px', cursor: 'help', minHeight: 160, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">DGB/USD Price</Typography>
                <Typography variant="h3" fontWeight="bold" sx={{ color: oraclePrice.price_micro_usd > 0 ? primaryColor : '#9e9e9e' }}>
                  {formatPrice(oraclePrice.price_micro_usd)}
                </Typography>
                {oraclePrice.price_micro_usd > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {oraclePrice.price_micro_usd.toLocaleString()} micro-USD
                  </Typography>
                )}
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={4}>
            <Tooltip title="Latest on-chain MuSig2 bundle signer count decoded from Core's participation bitmap. This is the actual 7-oracle signing set for the newest bundle." arrow placement="top">
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px', cursor: 'help', minHeight: 160, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">Oracle Consensus</Typography>
                <Typography variant="h3" fontWeight="bold" sx={{ color: latestBundleSignerCount > 0 ? primaryColor : '#9e9e9e' }}>
                  {oracleThreshold}/{oracleTotalSlots}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  signatures required
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ mt: 1, color: latestBundleSignerCount >= latestBundleRequired ? '#2e7d32' : '#ed6c02' }}>
                  {hasLatestBundleSignerData
                    ? `${latestBundleSignerCount}/${latestBundleRequired} signing`
                    : 'bundle signer data unavailable'}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={4}>
            <Tooltip title="Block height when the oracle price was last updated. Price becomes stale after 20 blocks without update" arrow placement="top">
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px', cursor: 'help', minHeight: 160, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">Last Update</Typography>
                <Typography variant="h4" fontWeight="bold" sx={{ color: oraclePrice.last_update_height > 0 ? primaryColor : '#9e9e9e' }}>
                  {oraclePrice.last_update_height > 0 ? `Block ${oraclePrice.last_update_height.toLocaleString()}` : 'No Data'}
                </Typography>
                {oraclePrice.last_update_height > 0 && (
                  <Chip
                    label={oraclePrice.is_stale ? 'Stale' : 'Fresh'}
                    color={oraclePrice.is_stale ? 'error' : 'success'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      )}

      <Box sx={{ mt: 2, p: 1.5, backgroundColor: `${primaryColor}12`, borderRadius: '8px', textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>{oracleConfig.releaseLabel}:</strong> {oracleConfig.phaseSummary} | {activeOracleCount} {oracleConfig.rosterLabel} oracles visible
        </Typography>
      </Box>
    </Card>
  );

  const OracleSitrepSection = () => (
    <Card elevation={3} sx={{ p: 3, mb: 4, borderRadius: '12px', borderTop: `4px solid ${consensusReady ? '#2e7d32' : '#ed6c02'}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <VerifiedIcon sx={{ fontSize: '2rem', color: consensusReady ? '#2e7d32' : '#ed6c02', mr: 1 }} />
          <Typography variant="h5" fontWeight="bold" sx={{ color: primaryColor }}>
            Oracle Network Status
          </Typography>
        </Box>
        <Chip
          label={consensusReady ? 'consensus ready' : 'below ready threshold'}
          color={consensusReady ? 'success' : 'warning'}
          size="small"
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={2.4}>
          <SitrepMetric
            label="Signing"
            value={`${latestBundleSignerCount}/${latestBundleRequired}`}
            detail={hasLatestBundleSignerData ? `actual 7-signer list from block ${latestBundle.height}` : 'waiting for getoraclesigners data'}
            ok={latestBundleSignerCount >= latestBundleRequired}
            progressValue={latestBundleSignerCount}
            progressTotal={latestBundleRequired}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SitrepMetric
            label="Live Price Feeds"
            value={`${priceSigningCount}/${activeOracleCount}`}
            detail={`${oracleThreshold} valid signed price feeds required`}
            ok={priceSigningCount >= oracleThreshold}
            progressValue={priceSigningCount}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SitrepMetric
            label="Online Heartbeats"
            value={`${freshHeartbeatCount}/${activeOracleCount}`}
            detail="signed operator status, fresh under 30 minutes"
            ok={freshHeartbeatCount >= oracleThreshold}
            progressValue={freshHeartbeatCount}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SitrepMetric
            label="Compatible Software"
            value={`${rc43ContextCount}/${activeOracleCount}`}
            detail={`MuSig2 context ${EXPECTED_MUSIG2_CONTEXT_VERSION}+ with valid heartbeat`}
            ok={rc43ContextCount >= oracleThreshold}
            progressValue={rc43ContextCount}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SitrepMetric
            label="Roster Oracles"
            value={`${activeOracleCount}/${oracleTotalSlots}`}
            detail={`configured ${oracleConfig.rosterLabel} from Core; ${locallyRunningCount} local oracle${locallyRunningCount === 1 ? '' : 's'} visible`}
            ok={activeOracleCount >= oracleThreshold}
            progressValue={activeOracleCount}
            progressTotal={oracleTotalSlots}
          />
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mt: 2 }}>
        Signing means this oracle actually signed the newest on-chain DigiDollar price bundle. Live price feed means the oracle is online and broadcasting a valid signed price. Roster oracles are the configured {oracleConfig.rosterLabel} loaded from Core.
      </Alert>

      {versionBuckets.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: primaryColor }}>
            Oracle Versions
          </Typography>
          <Grid container spacing={1.5}>
            {versionBuckets.map((bucket) => (
              <Grid item xs={12} sm={6} md={4} key={bucket.version}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: bucket.version === 'No version reported' ? '#fafafa' : 'rgba(46, 125, 50, 0.05)'
                  }}
                >
                  <Typography variant="body2" fontWeight="bold" sx={{ color: primaryColor }}>
                    {bucket.version}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {formatOperatorCount(bucket.count)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {bucket.reporting} live price feeds · {bucket.fresh} fresh heartbeats
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Card>
  );

  const OracleEpochSection = () => {
    if (!epochInfo) return null;

    const stateLabel = epochInfo.sessionState.replace(/_/g, ' ');
    const stateOk = epochInfo.sessionState === 'complete';

    return (
      <Card elevation={3} sx={{ p: 3, mb: 4, borderRadius: '12px', borderTop: `4px solid ${stateOk ? '#2e7d32' : '#ed6c02'}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HourglassTopIcon sx={{ fontSize: '2rem', color: stateOk ? '#2e7d32' : '#ed6c02', mr: 1 }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: primaryColor }}>
              Oracle Round Clock
            </Typography>
          </Box>
          <Chip
            label={`MuSig2 ${stateLabel}`}
            color={stateOk ? 'success' : 'warning'}
            size="small"
          />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{ p: 2, height: '100%', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <Typography variant="body2" color="text.secondary">Current Round</Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ color: primaryColor }}>
                Round {epochInfo.epoch.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Current block {epochInfo.currentHeight.toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{ p: 2, height: '100%', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <Typography variant="body2" color="text.secondary">Round Blocks</Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: primaryColor }}>
                Blocks {epochInfo.epochStartHeight.toLocaleString()}-{epochInfo.epochEndHeight.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {epochInfo.minedBlocks}/{ORACLE_EPOCH_BLOCKS} blocks observed
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{ p: 2, height: '100%', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <Typography variant="body2" color="text.secondary">Next Round</Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: primaryColor }}>
                Next round: block {epochInfo.nextEpochHeight.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {epochInfo.blocksUntilNextEpoch} blocks away
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDurationSeconds(epochInfo.estimatedSeconds)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{ p: 2, height: '100%', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <Typography variant="body2" color="text.secondary">Signing Progress</Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: stateOk ? '#2e7d32' : '#ed6c02', textTransform: 'capitalize' }}>
                MuSig2 {stateLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Nonces {epochInfo.nonceCount}/{epochInfo.requiredSigners}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Signatures {epochInfo.partialSigCount}/{epochInfo.requiredSigners}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={epochInfo.progress}
            color={stateOk ? 'success' : 'warning'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      </Card>
    );
  };

  // What Are Oracles Section
  const WhatAreOraclesSection = () => (
    <Card elevation={3} sx={{ p: 3, mb: 4, borderRadius: '12px' }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, color: primaryColor }}>
        What Are Oracles?
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>The Blockchain Blind Spot:</strong> Blockchains are isolated by design - they can't access
              the internet or "see" outside their own network. Without oracles, the blockchain has no idea what
              DGB is worth in USD.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>With Oracles:</strong> The blockchain receives verified price feeds from the real world.
              Now it knows: "1,000 DGB = $6.03 USD at block height 1,234,567"
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              How It Works:
            </Typography>
            <Box component="ol" sx={{ pl: 2, m: 0 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Oracles fetch prices from six active exchanges (Binance, KuCoin, Gate.io, HTX, Crypto.com, CoinGecko) every 60 seconds
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Calculate median price with MAD outlier filtering
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Sign price data with BIP-340 Schnorr signatures
              </Typography>
              <Typography component="li" variant="body2">
                Broadcast to P2P network - all nodes validate and relay
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Card>
  );

  // Become an Oracle Operator Section
  const BecomeOracleSection = () => (
    <Card elevation={3} sx={{ p: 3, mb: 4, borderRadius: '12px', backgroundColor: '#fafafa' }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, color: primaryColor }}>
        Become an Oracle Operator
      </Typography>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Help secure the DigiDollar network by running an {oracleConfig.operatorSlotLabel}.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', height: '100%', borderTop: `4px solid ${secondaryColor}` }}>
            <KeyIcon sx={{ fontSize: '3rem', color: secondaryColor, mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
              Step 1: Create Oracle Key
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Run <code>createoraclekey</code> in your DigiByte Core wallet to generate a secure keypair.
              The private key stays in your wallet - never leaves.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', height: '100%', borderTop: `4px solid ${secondaryColor}` }}>
            <SendIcon sx={{ fontSize: '3rem', color: secondaryColor, mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
              Step 2: Coordinate Slot Assignment
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Coordinate an assigned active oracle ID with the operator group before publishing a key.
              The live roster above shows every slot Core currently marks active for consensus.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', height: '100%', borderTop: `4px solid ${secondaryColor}` }}>
            <CloudDoneIcon sx={{ fontSize: '3rem', color: secondaryColor, mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
              Step 3: Start Oracle
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Once included in a release, run <code>startoracle</code> and your node
              will automatically fetch prices and broadcast to the network.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<DescriptionIcon />}
          href="https://github.com/DigiByte-Core/digibyte/blob/feature/digidollar-v1/DIGIDOLLAR_ORACLE_SETUP.md"
          target="_blank"
          sx={{
            backgroundColor: primaryColor,
            '&:hover': { backgroundColor: primaryColor }
          }}
        >
          Oracle Setup Guide
        </Button>
        <Button
          variant="outlined"
          startIcon={<GitHubIcon />}
          href="https://github.com/DigiByte-Core/digibyte/issues"
          target="_blank"
          sx={{
            borderColor: primaryColor,
            color: primaryColor
          }}
        >
          Coordinate Operator Slot
        </Button>
      </Box>
    </Card>
  );

  // Oracle List Table
  const OracleListSection = () => {
    return (
      <Card elevation={3} sx={{ p: 3, mb: 4, borderRadius: '12px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SecurityIcon sx={{ fontSize: '2rem', color: primaryColor, mr: 1 }} />
          <Typography variant="h5" fontWeight="bold" sx={{ color: primaryColor }}>
            {displayName} Oracle Network
          </Typography>
          {oracles.length > 0 ? (
            <Chip
              label={hasLatestBundleSignerData
                ? `${latestBundleSignerCount} / ${latestBundleRequired} Signing`
                : `${priceSigningCount} / ${activeOracleCount} Live Price Feeds`}
              color={(hasLatestBundleSignerData ? latestBundleSignerCount >= latestBundleRequired : priceSigningCount >= oracleThreshold) ? 'success' : 'warning'}
              size="small"
              sx={{ ml: 2 }}
            />
          ) : (
            <Chip
              label="Not Reporting"
              color="default"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={40} sx={{ color: primaryColor }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading oracle network...</Typography>
          </Box>
        ) : oracles.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <CloudOffIcon sx={{ fontSize: '3rem', color: '#9e9e9e', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No Oracle Data Available</Typography>
            <Typography variant="body2" color="text.secondary">Unable to fetch oracle network status</Typography>
          </Box>
        ) : (
          <>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', overflowX: 'visible' }}>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead sx={{ backgroundColor: `${primaryColor}12` }}>
              <TableRow>
                <TableCell sx={{ width: '15%' }}>
                  <Tooltip title="Oracle operator name and unique identifier" arrow>
                    <strong style={{ cursor: 'help' }}>Oracle</strong>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '12%' }}>
                  <Tooltip title="Reporting = actively broadcasting prices. No Data = not currently online" arrow>
                    <strong style={{ cursor: 'help' }}>Status</strong>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '13%' }}>
                  <Tooltip title="Current price this oracle is reporting to the network" arrow>
                    <strong style={{ cursor: 'help' }}>Price</strong>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '28%' }}>
                  <Tooltip title="Signed operator heartbeat and software/protocol versions reported by this oracle" arrow>
                    <strong style={{ cursor: 'help' }}>Heartbeat / Version</strong>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '14%' }}>
                  <Tooltip title="Signing means this oracle ID is in the newest on-chain MuSig2 participation bitmap and signed the latest bundle. Live price feed means it is currently broadcasting a valid signed price." arrow>
                    <strong style={{ cursor: 'help' }}>Signing / Live</strong>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '18%' }}>
                  <Tooltip title="P2P network address plus the shortened BIP-340 public key used to verify this oracle" arrow>
                    <strong style={{ cursor: 'help' }}>Endpoint</strong>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {oracles.map((oracle) => (
                <TableRow
                  key={oracle.oracle_id}
                  sx={{
                    backgroundColor: oracle.status === 'reporting' ? 'transparent' : 'rgba(0,0,0,0.02)',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: primaryColor }}>
                        {oracle.name || `Oracle ${oracle.oracle_id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {oracle.oracle_id}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {oracle.status === 'reporting' ? (
                        <CloudDoneIcon sx={{ color: '#4caf50' }} />
                      ) : (
                        <CloudOffIcon sx={{ color: '#9e9e9e' }} />
                      )}
                      <Chip
                        label={oracle.status === 'reporting' ? 'reporting' : 'no data'}
                        size="small"
                        color={oracle.status === 'reporting' ? 'success' : 'default'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ color: oracle.status === 'reporting' ? primaryColor : '#9e9e9e' }}
                    >
                      {oracle.status === 'reporting' ? formatPrice(oracle.price_micro_usd) : '--'}
                    </Typography>
                    {oracle.deviation_pct != null && oracle.deviation_pct !== 0 && (
                      <Typography variant="caption" color={Math.abs(oracle.deviation_pct) > 5 ? 'error' : 'text.secondary'}>
                        {oracle.deviation_pct > 0 ? '+' : ''}{oracle.deviation_pct.toFixed(2)}%
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {oracle.price_source !== 'none' ? oracle.price_source : 'no price source'} · {formatTimestampAge(oracle.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={oracle.heartbeat_status || 'unknown'}
                          size="small"
                          color={getHeartbeatChipColor(oracle.heartbeat_status, oracle.heartbeat_signature_valid)}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatAgeSeconds(oracle.heartbeat_age_seconds)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: oracle.software_version ? primaryColor : '#9e9e9e', overflowWrap: 'anywhere' }}>
                        {oracle.software_version || 'No version reported'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        MuSig2 ctx {oracle.musig2_context_version || '--'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Client {oracle.client_version || '--'} · P2P {oracle.p2p_protocol_version || '--'} · Oracle {oracle.oracle_protocol_version || '--'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Chip
                        label={oracle.signed_latest_bundle ? 'Signing' : 'Not signing'}
                        size="small"
                        color={oracle.signed_latest_bundle ? 'success' : 'default'}
                        variant={oracle.signed_latest_bundle ? 'filled' : 'outlined'}
                      />
                      <Chip
                        label={isOracleSigningPrice(oracle) ? 'Live price feed' : 'No live price'}
                        size="small"
                        color={isOracleSigningPrice(oracle) ? 'success' : 'default'}
                        variant="outlined"
                      />
                      {oracle.is_running_locally && (
                        <Chip label="local" size="small" color="info" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', overflowWrap: 'anywhere', lineHeight: 1.4 }}>
                      {oracle.endpoint}
                    </Typography>
                    {oracle.pubkey ? (
                      <Tooltip title={`${oracle.pubkey} (Click to view in source code)`}>
                        <Link
                          href="https://github.com/DigiByte-Core/digibyte/blob/feature/digidollar-v1/src/kernel/chainparams.cpp"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            textDecoration: 'none',
                            color: primaryColor,
                            '&:hover': {
                              textDecoration: 'underline',
                              color: secondaryColor
                            }
                          }}
                        >
                          {oracle.pubkey.substring(0, 10)}...{oracle.pubkey.substring(oracle.pubkey.length - 8)}
                        </Link>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">--</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Price Format:</strong> Oracle prices use micro-USD format where 1,000,000 = $1.00.
            This ensures exact arithmetic with no floating-point errors. Consensus is {oracleConsensusLabel} across {oracleTotalSlots} reserved slots, with {activeOracleCount} {oracleConfig.rosterLabel} oracles displayed above.
          </Typography>
        </Box>
        </>
        )}
      </Card>
    );
  };

  // Technical Details Section
  const TechnicalSection = () => (
    <Card elevation={3} sx={{ p: 3, mb: 4, borderRadius: '12px' }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, color: primaryColor }}>
        Technical Specifications
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
              {displayName} Oracle Configuration
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>{oracleThreshold}-signature oracle quorum</Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>{oracleConsensusLabel} reserved oracle slots required for consensus, with {activeOracleSlots} {oracleConfig.rosterLabel} oracles</Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>{network.digiDollarRelease.version} on {network.digiDollarRelease.network}, P2P port {network.digiDollarRelease.p2pPort}</Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>MuSig2 aggregate signing (v0x03) only</Typography>
              <Typography component="li" variant="body2">BIP9 deployment status comes from the local Core RPC feed</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
              Shared Production Rules
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>MuSig2 aggregate signing (v0x03)</Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>35-slot oracle roster with 7 of 35 signature quorum</Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>BIP9 activation for deployment</Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Exchange fetch and oracle broadcast every 60 seconds</Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Compact MuSig2 v0x03 bundle per block (~91 bytes: participation bitmap, price, timestamp, 64-byte aggregate signature)</Typography>
              <Typography component="li" variant="body2">Production oracle endpoints</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Price Validation Limits:
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip label="Min: $0.0001/DGB (100 micro-USD)" variant="outlined" />
          <Chip label="Max: $100.00/DGB (100M micro-USD)" variant="outlined" />
          <Chip label="Valid for 20 blocks" variant="outlined" />
        </Box>
      </Box>
    </Card>
  );

  const NotActiveBanner = () => {
    if (!ddDeploymentStatus || ddDeploymentStatus === 'active') return null;
    return (
      <Alert
        severity="info"
        sx={{ mb: 3, borderRadius: '12px' }}
        action={
          <Button color="inherit" size="small" component={RouterLink} to={network.getNetworkPath('/activation')}>
            Track Activation →
          </Button>
        }
      >
        <Typography variant="body1">
          <strong>DigiDollar is not active yet.</strong> Currently in the <strong>{ddDeploymentStatus.toUpperCase().replace('_', ' ')}</strong> stage of BIP9 activation.
        </Typography>
      </Alert>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <HeroSection />
      <NotActiveBanner />
      <CurrentPriceCard />
      <OracleEpochSection />
      <OracleSitrepSection />
      <OracleListSection />
      <WhatAreOraclesSection />
      <BecomeOracleSection />
      <TechnicalSection />
      <IntegrationGuides />
    </Container>
  );
};

export default OraclesPage;
